import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const FIAT_SCOPE = ["Supply chain workflows", "Online buying behavior", "Multichannel retail"];

export default function CaseStudies() {
  return (
    <>
      <SEO
        title="Case Studies — Vector Growth and Advisory"
        description="A commercial market assessment for FIAT Automobile, 30%+ pipeline growth for European SMEs and tech distributors, and 22% faster lead-to-opportunity conversion."
      />
      <section className="relative overflow-hidden pt-40 pb-16 md:pt-48" data-testid="cases-hero">
        <div className="spot pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
          <Eyebrow>Selected work</Eyebrow>
          <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl" data-testid="cases-headline">
            Proof, <span className="text-gradient">not promises.</span>
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-16 md:px-10" data-testid="fiat-case">
        <Reveal>
          <div className="grid items-stretch gap-px overflow-hidden rounded-2xl border hairline bg-azure/10 lg:grid-cols-2">
            <div className="relative min-h-[320px] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1765475886628-a7c1fa36b0af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxmaWF0JTIwYXV0b21vYmlsZSUyMGNhciUyMHNob3dyb29tJTIwbW9kZXJuJTIwRXVyb2BlYW4lMjB2ZWhpY2xlfGVufDB8fHx8MTc5MTU1NTczNXww&ixlib=rb-4.1.0&q=85"
                alt="FIAT automobile"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="fiat-case-image"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-ink/40 to-transparent" />
              <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.28em] text-neon">
                Case file 001 — Automotive · Enterprise
              </p>
            </div>
            <div className="bg-surface/80 p-8 md:p-12">
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-neon/90">Commercial Market Assessment</p>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">FIAT Automobile</h2>
              <p className="mt-6 text-sm leading-relaxed text-slate-600 md:text-base">
                A completed commercial market assessment covering supply chain workflows, online
                buying behavior and multichannel retail — mapping where enterprise momentum is
                lost between plant, platform and dealer network.
              </p>
              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="fiat-scope-chips">
                {FIAT_SCOPE.map((s) => (
                  <span key={s} className="rounded-full border hairline bg-elevated px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-600">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 md:px-10 md:pb-32" data-testid="results-cases">
        <div className="grid gap-5 md:grid-cols-2">
          <Reveal className="h-full">
            <div className="flex h-full flex-col justify-between rounded-2xl border hairline bg-surface/70 p-8 md:p-12" data-testid="case-pipeline-growth">
              <div>
                <p className="font-display text-6xl font-extrabold text-gradient md:text-7xl">30%+</p>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight">European SMEs & tech distributors</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-500">
                  Built digital acquisition funnels, paid campaigns and SEO and content plans —
                  growing client sales pipelines by more than 30% across engagements.
                </p>
              </div>
              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Aggregated results across engagements
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="h-full">
            <div className="flex h-full flex-col justify-between rounded-2xl border hairline bg-surface/70 p-8 md:p-12" data-testid="case-crm-acceleration">
              <div>
                <p className="font-display text-6xl font-extrabold text-gradient md:text-7xl">22%</p>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight">AI automation & Salesforce CRM</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-500">
                  Set up AI-assisted marketing automation and Salesforce CRM pipelines — lowering
                  acquisition cost and speeding up lead-to-opportunity conversion by 22%.
                </p>
              </div>
              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Marketing automation · CRM architecture
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-20 text-center">
          <p className="mx-auto max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl">
            Your case file is the next one.
          </p>
          <Link
            to="/contact"
            data-testid="cases-cta-button"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-all duration-300 hover:shadow-[0_0_45px_rgba(47,168,224,0.45)]"
          >
            Start the conversation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
